import { Activity, ArrowLeft, ArrowRight, Boxes, Sparkles, Upload, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Button } from './ui/Button.js';

const STORAGE_KEY = 'opensentry.welcome.dismissed';

export function welcomeWasDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** ?welcome=true forces the modal open on every load, ignoring dismissal. */
export function welcomeForced(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('welcome') === 'true';
  } catch {
    return false;
  }
}

const SOURCES =
  'USGS seismic · NWS weather · GDACS disasters · NASA FIRMS wildfire · GDELT conflict · ReliefWeb humanitarian · US State Dept travel advisories';

function Screen1() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[var(--color-sev-high)]">
        <Activity size={18} />
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--color-muted)]">
          Operational threat intelligence
        </span>
      </div>
      <h2 className="text-2xl font-semibold tracking-tight">
        One operational picture of risk to your people.
      </h2>
      <p className="text-sm leading-relaxed text-[var(--color-muted)]">
        OpenSentry fuses{' '}
        <span className="text-[var(--color-text)]">
          seven authoritative, real-time intelligence streams
        </span>{' '}
        into a single common operating picture, then spatially joins every event against where your
        workforce actually is — so the question isn’t “what happened?”, it’s{' '}
        <span className="text-[var(--color-text)]">“who is exposed, and what do we do?”</span>
      </p>
      <ul className="space-y-2 text-sm">
        <li className="flex gap-2">
          <span className="text-[var(--color-sev-low)]">▸</span> Multi-source fusion, not a single
          feed — {SOURCES}.
        </li>
        <li className="flex gap-2">
          <span className="text-[var(--color-sev-low)]">▸</span> Geospatial join (H3) turns raw
          events into <span className="text-[var(--color-text)]">exposure</span> per workforce zone.
        </li>
        <li className="flex gap-2">
          <span className="text-[var(--color-sev-low)]">▸</span> An AI layer turns multi-stream
          noise into a <span className="text-[var(--color-text)]">decision</span> — and every event
          is archived with its source for full traceability.
        </li>
      </ul>
      <p className="text-xs text-[var(--color-muted)]">
        Built as operational software — running continuously on a serverless edge, not a one-off
        demo — for decisions where quality has real consequences.
      </p>
    </div>
  );
}

function Stage({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-2)] px-3 py-2">
      <div className="flex items-baseline gap-2">
        <span className="mono text-[10px] text-[var(--color-sev-high)]">{n}</span>
        <span className="text-xs font-semibold">{title}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded border border-[var(--color-border)] bg-[var(--color-panel)] px-1.5 py-0.5 text-[10px] text-[var(--color-muted)]">
      {children}
    </span>
  );
}

function Arrow() {
  return <div className="my-0.5 text-center text-xs leading-none text-[var(--color-muted)]">↓</div>;
}

/** Inline vector flow — guaranteed present, no external asset/build step. */
function ArchitectureDiagram() {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
      <Stage n={1} title="Ingest — scheduled Cloudflare Workers (cron)">
        <Chip>USGS</Chip>
        <Chip>NWS</Chip>
        <Chip>GDACS</Chip>
        <Chip>FIRMS</Chip>
        <Chip>GDELT</Chip>
        <Chip>ReliefWeb</Chip>
        <Chip>State Dept</Chip>
        <Chip>→ normalize · KV dedupe · R2 archive</Chip>
      </Stage>
      <Arrow />
      <Stage n={2} title="Fuse — Cloudflare D1">
        <Chip>events store</Chip>
        <Chip>H3 geospatial join</Chip>
        <Chip>→ workforce zones · exposure</Chip>
      </Stage>
      <Arrow />
      <Stage n={3} title="Reason — AI Gateway (cached, cost-metered)">
        <Chip>Workers AI / Claude</Chip>
        <Chip>zone posture</Chip>
        <Chip>match rationale</Chip>
        <Chip>situation brief</Chip>
        <Chip>conflict triage</Chip>
      </Stage>
      <Arrow />
      <Stage n={4} title="Serve — Hono API on Cloudflare Workers">
        <Chip>React + MapLibre console</Chip>
        <Chip>map · drawers · travel lookup</Chip>
      </Stage>
      <div className="mt-2 text-center text-[10px] uppercase tracking-widest text-[var(--color-muted)]">
        Serverless edge — no servers to operate
      </div>
    </div>
  );
}

function Screen2() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[var(--color-sev-high)]">
        <Boxes size={18} />
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--color-muted)]">
          How it works
        </span>
      </div>
      <h2 className="text-2xl font-semibold tracking-tight">Edge-native ingestion → fusion → AI</h2>
      <ArchitectureDiagram />
      <ol className="space-y-1.5 text-sm text-[var(--color-muted)]">
        <li>
          <span className="text-[var(--color-text)]">1. Ingest</span> — scheduled Workers pull each
          source on its own cadence, normalize to one event schema, dedupe (KV), archive raw (R2).
        </li>
        <li>
          <span className="text-[var(--color-text)]">2. Fuse</span> — events land in D1 and are
          spatially joined to H3 workforce zones to compute exposure.
        </li>
        <li>
          <span className="text-[var(--color-text)]">3. Reason</span> — an AI layer (Workers AI /
          Claude via AI Gateway, cached) writes per-zone posture, match rationale, and a situation
          brief.
        </li>
        <li>
          <span className="text-[var(--color-text)]">4. Serve</span> — a Hono API on Cloudflare
          Workers feeds this React + MapLibre console. No servers to operate.
        </li>
      </ol>
    </div>
  );
}

function Screen3() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[var(--color-sev-high)]">
        <Upload size={18} />
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--color-muted)]">
          Get started
        </span>
      </div>
      <h2 className="text-2xl font-semibold tracking-tight">Three ways to put data on the map</h2>
      <div className="space-y-3 text-sm">
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3">
          <div className="mb-1 font-semibold">1 · Load demo data</div>
          <p className="text-[var(--color-muted)]">
            The <span className="text-[var(--color-text)]">Load demo data</span> button seeds a
            synthetic global workforce plus a curated multi-source event set so the map is populated
            instantly — nothing to configure.
          </p>
        </div>
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3">
          <div className="mb-1 font-semibold">2 · Upload your workforce</div>
          <p className="text-[var(--color-muted)]">
            <span className="text-[var(--color-text)]">Employees → Import</span> takes a CSV (name,
            location or lat/lng). Each person is geocoded into an H3 zone and joined against live
            events.
          </p>
        </div>
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3">
          <div className="mb-1 font-semibold">3 · Look up any location</div>
          <p className="text-[var(--color-muted)]">
            <span className="text-[var(--color-text)]">Travel Lookup</span> takes an address or
            “lat, lng” and returns nearby risk plus an AI travel brief — for a trip or a new site.
          </p>
        </div>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-[var(--color-muted)]">
        <Sparkles size={12} className="text-[var(--color-sev-high)]" /> Click any hexagon (a
        workforce zone) or marker (an event) for detail and an AI explanation.
      </p>
    </div>
  );
}

const SCREENS = [Screen1, Screen2, Screen3];

export function WelcomeModal({ onClose }: { onClose: () => void }) {
  const [i, setI] = useState(0);
  const Body = SCREENS[i]!;
  const last = i === SCREENS.length - 1;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 h-full w-full cursor-default bg-black/60"
        onClick={dismiss}
      />
      <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3">
          <span className="text-sm font-semibold tracking-tight">OpenSentry</span>
          <button
            type="button"
            onClick={dismiss}
            className="rounded p-1 text-[var(--color-muted)] hover:bg-[var(--color-panel-2)]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5">
          <Body />
        </div>
        <div className="flex items-center justify-between border-t border-[var(--color-border)] px-5 py-3">
          <div className="flex gap-1.5">
            {SCREENS.map((_, idx) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static stepper
                key={idx}
                className="h-1.5 w-6 rounded-full"
                style={{
                  background: idx === i ? 'var(--color-sev-high)' : 'var(--color-border)',
                }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {i > 0 && (
              <Button variant="ghost" onClick={() => setI(i - 1)}>
                <ArrowLeft size={14} /> Back
              </Button>
            )}
            {last ? (
              <Button variant="primary" onClick={dismiss}>
                Get started
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setI(i + 1)}>
                Next <ArrowRight size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
